import { apiClient } from './config.js';

export interface ToolRunSubmission {
  run_id: string;
  tool_name: string;
  dry_run: boolean;
  status: string;
}

export interface ToolRunStatus {
  run_id: string;
  status: string;
  tool_name: string;
  dry_run: boolean;
  result: Record<string, any> | null;
  error: string | null;
  /**
   * Live progress for tools that report it. `GET /tools/runs/{run_id}`
   * spreads the whole in-memory run state, so this is whatever shape the
   * tool writes — polling is what carries progress (the WebSocket event
   * carries only the status transition).
   */
  progress?: Record<string, any> | null;
}

export async function runBackfillImageHashes(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-image-hashes',
    null,
    { params: { dry_run: dryRun } }
  );
  return response.data;
}

/**
 * Seed cynapse.image_entity_associations from assayer
 * announcements.entity_id. 2026-05-15 one-shot fix for the empty
 * entity-pool table — Phase 1's editorial selector reads that table
 * and was finding nothing because the PDF-extract path never wrote
 * to it. Walks all announcements with non-null entity_id, sends
 * (announcement_id, entity_id) pairs to cynapse which joins to
 * image_announcement_associations to find images and bulk-inserts
 * into image_entity_associations.
 */
export async function runBackfillEntityAssociations(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-entity-associations',
    null,
    { params: { dry_run: dryRun } }
  );
  return response.data;
}

/**
 * Replay the Cynapse company binder + Phase B + deep_backsource
 * orchestrator for events stuck in state='saturating' with
 * saturation_jobs_total=0. Use after the M&A research-gate fix to
 * recover historical M&A announcements that opened gates which never
 * resolved counterparties.
 */
export async function runBackfillStuckResearchEvents(
  dryRun: boolean,
  options: { limit?: number } = {},
): Promise<ToolRunSubmission> {
  const params: Record<string, string | number | boolean> = { dry_run: dryRun };
  if (options.limit) params.limit = options.limit;
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-stuck-research-events',
    null,
    { params },
  );
  return response.data;
}

/**
 * E0 — halt document acquisition backfill
 * (CROSS_VENUE_EVENT_LIFECYCLE_PLAN §4b).
 *
 * A trading halt states its own purpose — "pending an announcement … in
 * relation to a potential change of control transaction" — and we have
 * never read it: the halt signal is written from the TITLE and the request
 * letter is never fetched. Measured 2026-08-18, 608 of 711 halt-class
 * signals carry no announcement at all.
 *
 * Enqueues one document fetch per title-only halt signal (no article is
 * ever generated). Spend is one PDF fetch + one Prism extraction each, so
 * `limit` takes the backlog in deliberate tranches; the result reports how
 * many remain so a bounded run is never mistaken for a complete one.
 * Idempotent — a re-run skips anything already enqueued.
 */
export async function runBackfillHaltDocuments(
  dryRun: boolean,
  options: { limit?: number } = {},
): Promise<ToolRunSubmission> {
  const params: Record<string, string | number | boolean> = { dry_run: dryRun };
  if (options.limit) params.limit = options.limit;
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-halt-documents',
    null,
    { params },
  );
  return response.data;
}

/**
 * E1 — re-extract the halt documents we already hold
 * (CROSS_VENUE_EVENT_LIFECYCLE_PLAN §5).
 *
 * The other half of the halt-document backfill. E0 fetches documents we don't
 * have; this re-runs extraction over the ones we do, which were classified
 * before the Stated-Purpose fields existed and would otherwise never carry
 * one — including the Ausgold request that started this work. One Prism
 * classification call each; no article is generated.
 */
export async function runReextractHaltDocuments(
  dryRun: boolean,
  options: { limit?: number } = {},
): Promise<ToolRunSubmission> {
  const params: Record<string, string | number | boolean> = { dry_run: dryRun };
  if (options.limit) params.limit = options.limit;
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/reextract-halt-documents',
    null,
    { params },
  );
  return response.data;
}

/**
 * E1 — Stated-Purpose calibration (CROSS_VENUE_EVENT_LIFECYCLE_PLAN §5).
 *
 * Read-only. Reports the distribution of extracted halt purpose classes over
 * every halt signal that has a document behind it, plus how many halts have
 * no document at all — a distribution means nothing without the coverage it
 * silently excludes. Every change_of_control hit comes back with its
 * verbatim span, because that is the class that opens a watch and publishes:
 * it gets checked by eye, not by percentage.
 *
 * Nothing should rely on the class until this has been read.
 */
export async function runHaltPurposeCalibration(): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/halt-purpose-calibration',
    null,
  );
  return response.data;
}

/**
 * Marketfloor coverage rollout — Verification §5. Re-classifies a sample of
 * recent announcements through the real Prism path and diffs the new sector
 * against the stored one (confirms the §8 Consumer/Industrials/Utilities
 * sectors are assigned, no drift on existing buckets, Energy→Utilities).
 * Read-only — does not re-write history. One Gemini extraction per sample.
 */
export async function runPrismTaxonomyEval(sampleSize: number = 12): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/prism-taxonomy-eval',
    null,
    { params: { sample_size: sampleSize } }
  );
  return response.data;
}

/**
 * Marketfloor coverage rollout — Verification §1. Reconstructs marketfloor's
 * discovery candidate pool for today and compares the baseline ranking vs the
 * rebalanced ranking the broad_target_balance re-rank would produce. Override
 * params simulate the proposed seed before it is applied (migration 188).
 * Read-only — nothing enqueued.
 */
export async function runDiscoveryRerankBacktest(opts?: {
  targetName?: string;
  nonresourceBoost?: number;
  topN?: number;
}): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/discovery-rerank-backtest',
    null,
    {
      params: {
        target_name: opts?.targetName ?? 'marketfloor',
        nonresource_boost: opts?.nonresourceBoost ?? 0.15,
        top_n: opts?.topN ?? 25,
      },
    }
  );
  return response.data;
}

/**
 * Combined social-draft cleanup. Regenerates frontmatter-leaked drafts
 * (re-composes through the fixed stripper) and skips drafts whose article is
 * archived (dead duplicates). Dry run previews counts; execute applies.
 * Live-site link realignment is handled by Sweep-Refresh Live Articles.
 */
export async function runReconcileSocialDrafts(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/reconcile-social-drafts',
    null,
    { params: { dry_run: dryRun } }
  );
  return response.data;
}

/**
 * Repair intelligence_signals dated to a session they did not happen in
 * (CROSS_VENUE_EVENT_LIFECYCLE_PLAN §6.6, O9 + O11).
 *
 * Two lanes stamped `event_date` from the wall clock rather than the market
 * session; both are fixed forward, and this clears what they already wrote.
 * Dry-run reports each cohort separately so the operator sees exactly what
 * `dry_run=false` executes:
 *
 *   redate 42                       — holiday-dated scanner signals that exist
 *                                     nowhere else; 43 of the 47 candidates are
 *                                     filings lodged after 16:35 on Friday
 *                                     2026-06-05, inside the after-close hole.
 *                                     Re-dated from the linked scan row's real
 *                                     announcement_time, never a constant.
 *   collision_delete 5              — cannot be re-dated: the unique index on
 *                                     (signal_type, ticker, event_date) ignores
 *                                     `label`, so a 06-05 row holds the slot.
 *   o9_duplicates 139               — exact duplicates of correctly-dated rows.
 *   spike_never_reseen 926          — price spikes created before the 10:00
 *                                     open that no live session ever confirmed.
 *   spike_reseen_still_matching 449 — re-seen but still carrying the previous
 *                                     day's exact value; in-session rows match
 *                                     the prior day only 0.8% of the time
 *                                     (33/4,050) against 64.5% pre-open.
 *
 * 244 pre-open spikes that behave like live data are deliberately KEPT.
 * Deletes NULL announcement_scans.intelligence_signal_id first (NO ACTION FK),
 * which permanently severs re-derivation — operator-ruled dead records.
 * Idempotent: a second run reports zeros.
 */
export async function runRepairMisdatedSignals(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/repair-misdated-signals',
    null,
    { params: { dry_run: dryRun } },
  );
  return response.data;
}

/**
 * Re-derive title signals that collapsed into one row per (type, ticker, day)
 * (BC8 incident, 2026-09-07).
 *
 * Until mig 336 the unmatched unique key on intelligence_signals was the
 * COARSE signal_type, so same-day filings of different kinds (a director
 * cessation and a director appointment both map to regulatory_filing)
 * overwrote each other and only the last title written survived. Every
 * announcement_scans row kept its FK to the one survivor — 550 scans across
 * 325 tickers at the time of the fix. The scan rows still carry everything the
 * writer needs, so this re-derives the missing signal per scan, re-points the
 * scan FK, and binds the new row to the same-day announcement whose title
 * matches. Nothing is deleted. Refuses to run before mig 336 is applied.
 */
export async function runRepairCollapsedSignals(
  dryRun: boolean,
  limit = 1000,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/repair-collapsed-signals',
    null,
    { params: { dry_run: dryRun, limit } },
  );
  return response.data;
}

/**
 * Headline cards for recent heroless articles whose social posts have not
 * yet been pushed (2026-09-16). The card hook reached the worker generate
 * path in backend rev 00922 after a night of articles had been stored
 * without one; cards never retroact and the publishing side fetches media at
 * push time, so posts still in the stagger hold would ship the masthead
 * default. Dry-run lists the cohort. Idempotent.
 */
export async function runBackfillHeadlineCards(
  dryRun: boolean,
  days = 3,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-headline-cards',
    null,
    { params: { dry_run: dryRun, days } },
  );
  return response.data;
}

/**
 * Identity-change backlog (2026-09-23). Replays scanned rename / re-ticker
 * filings, pairs the two codes of a re-ticker, and raises one identity-change
 * candidate against the PREDECESSOR carrying new_code. Nothing is applied.
 */
export async function runBackfillIdentityChanges(
  dryRun: boolean,
  since = '2026-04-29',
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-identity-changes',
    null,
    { params: { dry_run: dryRun, since } },
  );
  return response.data;
}

/**
 * Securities-master seeding (2026-09-23). Writes each covered issuer's current
 * ASX code window so the temporal resolver stops failing open to a stub.
 */
export async function runSeedSecuritiesMaster(
  dryRun: boolean,
  limit = 500,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/seed-securities-master',
    null,
    { params: { dry_run: dryRun, limit } },
  );
  return response.data;
}

/**
 * Cross-ticker co-party backfill (M&A Track C1, ruling 2026-09-21). Replays
 * recent cross-ticker dedupe results through the live record → promote path
 * (joint-shaped classes only), then republishes published articles whose
 * frontmatter predates the co_lodged row so both hubs show them. Republish
 * lands on TEST. Dry-run lists both cohorts. Idempotent.
 */
export async function runBackfillCrossTickerCoParties(
  dryRun: boolean,
  days = 30,
  republish = true,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-cross-ticker-co-parties',
    null,
    { params: { dry_run: dryRun, days, republish } },
  );
  return response.data;
}

/**
 * LinkedIn API recheck of held URNs (Milestone A backfills B1 + B2,
 * LINKEDIN_CM_API_INTEGRATION_PLAN §4.4). One vanity lookup per row; stops
 * cleanly when the day's finder budget is spent; resumable.
 */
export async function runReverifyLinkedInUrns(
  dryRun: boolean,
  limit = 100,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/reverify-linkedin-urns',
    null,
    { params: { dry_run: dryRun, limit, pace_seconds: 2 } },
  );
  return response.data;
}

/**
 * Backfill B3: flag scraper-era needs_review rows that already hold a slug
 * so the resolver's normal loop drains them at the queue budget. Synchronous
 * on the backend; it registers a completed run so the tile reads it back.
 */
export async function runLinkedInFlagApiRecheck(
  dryRun: boolean,
  limit = 200,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/linkedin-flag-api-recheck',
    null,
    { params: { dry_run: dryRun, limit } },
  );
  return response.data;
}

/**
 * LinkedIn Page analytics lane (P6 / Network Metrics M5, CM plan §8b).
 * Dry run = the spike: calls LinkedIn for the three masthead Pages and reports
 * each response's structure, writes nothing. Execute = enqueue a durable
 * network_metrics_sweep job for the LinkedIn lane.
 */
export async function runLinkedInPageAnalytics(
  dryRun: boolean,
  fullBackfill = false,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/linkedin-page-analytics',
    null,
    { params: { dry_run: dryRun, full_backfill: fullBackfill } },
  );
  return response.data;
}

/**
 * Pipeline health (mig 397) — the system measuring itself: articles generated,
 * held at generation, duplicate (announcement, product) pairs and guard skips,
 * per masthead per UTC day. Dry run = compute the last 7 days now, write
 * nothing. Execute = enqueue the sweep's `pipeline` lane (durable job).
 */
export async function runPipelineHealth(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/pipeline-health',
    null,
    { params: { dry_run: dryRun } },
  );
  return response.data;
}

/** Enqueue a network-metrics collection run (durable pipeline job). */
export async function runNetworkMetricsSweep(
  lanes?: string,
  fullBackfill = false,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/network-metrics-sweep',
    null,
    { params: { lanes, full_backfill: fullBackfill } },
  );
  return response.data;
}

/**
 * Exchange reference data — propose / confirm / hours / readiness
 * (EXCHANGE_REFERENCE_DATA_PLAN).
 *
 * Session hours and market closures decide which trading day a filing belongs
 * to, so wrong reference data is the 2026-06-08 wrong-date bug at the calendar
 * layer, where it poisons every lane at once. The loop is therefore
 * interrupted by a person by design: propose stages a proposal as
 * confidence='forecast' (invisible to the live session path) and returns a
 * diff; confirm promotes it.
 *
 * `url` must be the EXCHANGE'S OWN published page — an aggregator is not
 * authoritative. It is stored on confirm so later runs re-fetch what was
 * actually verified.
 */
export async function runExchangeReferencePropose(
  exchange: string,
  url: string,
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/exchange-reference/propose',
    null,
    { params: { exchange, url } },
  );
  return response.data;
}

/**
 * Promote a staged proposal.
 *
 * `confirmRemovals` is a separate decision: dropping a closure re-opens a
 * market that is shut, and a page that failed to render its table looks
 * identical to a page with no holidays.
 *
 * `exclude` (comma-separated ISO dates) rejects specific entries — an
 * exchange's public-holiday page legitimately lists dates that are not market
 * closures. The live case: ASX lists Anzac Day 2026, which falls on a
 * Saturday. See the diff's `weekend_dated_additions`.
 */
export async function runExchangeReferenceConfirm(
  exchange: string,
  opts?: { confirmRemovals?: boolean; exclude?: string },
): Promise<Record<string, unknown>> {
  const response = await apiClient.post<Record<string, unknown>>(
    '/tools/exchange-reference/confirm',
    null,
    {
      params: {
        exchange,
        confirm_removals: opts?.confirmRemovals ?? false,
        ...(opts?.exclude ? { exclude: opts.exclude } : {}),
      },
    },
  );
  return response.data;
}

/**
 * Confirm a venue's session hours against a named source. Separate from the
 * closure confirm because hours change almost never and closures change
 * yearly — bundling them would let the yearly act quietly rewrite the hours.
 */
export async function runExchangeReferenceSetHours(
  exchange: string,
  marketOpen: string,
  marketClose: string,
  sourceUrl: string,
): Promise<Record<string, unknown>> {
  const response = await apiClient.post<Record<string, unknown>>(
    '/tools/exchange-reference/set-hours',
    null,
    {
      params: {
        exchange,
        market_open: marketOpen,
        market_close: marketClose,
        source_url: sourceUrl,
      },
    },
  );
  return response.data;
}

export interface ExchangeReadinessRow {
  code: string;
  display_name: string;
  has_timezone: boolean;
  has_hours: boolean;
  hours_verified_at: string | null;
  hours_source_url: string | null;
  announcements: number;
  closures_this_year: number;
  forms: number;
  ingesting: boolean;
  ready: boolean;
  verdict: string;
}

/**
 * Read-only. "Venue is live" is an unenforced conjunction of adapter +
 * timezone + hours + current-year closures + forms; this makes it visible.
 * The row that matters is a venue with announcements flowing and no session
 * calendar — 12 venues were in that state on 2026-08-19.
 */
export async function getExchangeReadiness(): Promise<{
  exchanges: ExchangeReadinessRow[];
  alerting: string[];
  alerting_count: number;
}> {
  const response = await apiClient.get<{
    exchanges: ExchangeReadinessRow[];
    alerting: string[];
    alerting_count: number;
  }>('/tools/exchange-reference/readiness');
  return response.data;
}

export async function getToolRunStatus(runId: string): Promise<ToolRunStatus> {
  const response = await apiClient.get<ToolRunStatus>(`/tools/runs/${runId}`);
  return response.data;
}

/**
 * Poll an async /tools run until it completes (2026-09-22). Long jobs run in
 * the background because the gateway cuts a request at 60 s; `onProgress`
 * receives each status (its `progress` is whatever the tool reports).
 * Resolves with the run's `result`; rejects with its error.
 */
export async function waitForToolRun<T = Record<string, any>>(
  runId: string,
  onProgress?: (status: ToolRunStatus) => void,
  intervalMs = 2000,
): Promise<T> {
  for (;;) {
    const status = await getToolRunStatus(runId);
    onProgress?.(status);
    if (status.status === 'completed') return status.result as T;
    if (status.status === 'failed') throw new Error(status.error || 'Tool run failed');
    await new Promise(r => setTimeout(r, intervalMs));
  }
}

/** Submission shape for tools that carry no dry_run (substance gate tools). */
export interface ToolRunSubmissionLite {
  run_id: string;
  tool_name: string;
  status: string;
}

/**
 * SUBSTANCE_RECALIBRATION gate (2026-08-05). Re-scores a random sample of
 * article-producing announcements on the widened v2 (1-5) scale — ALWAYS v2,
 * regardless of the assayer_substance_scale_v2_enabled flag — and returns the
 * old→new distribution + transition matrix. This is the diff the operator
 * reviews BEFORE flipping generation to v2. Read-only.
 */
export async function runSubstanceRescorePreview(
  sampleSize = 60,
  days = 21,
): Promise<ToolRunSubmissionLite> {
  const response = await apiClient.post<ToolRunSubmissionLite>(
    '/tools/substance-rescore-preview',
    null,
    { params: { sample_size: sampleSize, days } },
  );
  return response.data;
}

/**
 * SUBSTANCE_RECALIBRATION guardrail (2026-08-05). Trailing-window
 * editorial_substance distribution + collapse verdict (a single band
 * dominating the scored pool). Read-only.
 */
export async function runSubstanceDistributionCheck(
  days = 14,
  articlePoolOnly = true,
): Promise<ToolRunSubmissionLite> {
  const response = await apiClient.post<ToolRunSubmissionLite>(
    '/tools/substance-distribution-check',
    null,
    { params: { days, article_pool_only: articlePoolOnly } },
  );
  return response.data;
}

/**
 * Re-render every live article on the editorial pub targets (orecast,
 * bellwire, themarketfloor) and ship the changes to main as a single
 * batched PR per pub. Used after a template or publishing-service change
 * that needs to propagate to existing pages.
 *
 * Selectivity guarantees:
 *  - Only articles already live on each pub's main branch are touched.
 *    Test-only drafts under operator review are skipped.
 *  - The promote PR contains ONLY the swept article paths — other
 *    commits sitting on test stay there. Wholesale promote is NOT used.
 *
 * Dry-run returns per-target counts. Non-dry runs the full sweep+promote
 * inline (one synchronous request per pub, ~30–120s each).
 *
 * `forceReslug=true` regenerates slugs via the optimised SEO slug
 * generator and writes a `_redirects.json` map into each pub so the
 * Worker 301s every historical URL to the current one. One-time
 * migration use; new articles use the optimised slugger by default
 * thereafter.
 */
export async function runSweepAndPromote(
  dryRun: boolean,
  options: { forceReslug?: boolean } = {},
): Promise<ToolRunSubmission> {
  const params: Record<string, string | number | boolean> = { dry_run: dryRun };
  if (options.forceReslug) params.force_reslug = true;
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/sweep-and-promote',
    null,
    { params },
  );
  return response.data;
}

/**
 * Unpublish duplicate-slug articles left stranded by the 2026-05-25
 * saturation cascade. For every (announcement, content_product) pair
 * with more than one ``published_slug`` in the articles table since
 * the cut-off date, identifies a canonical row (most-recently-
 * published, fallback to most-recently-generated) and enqueues an
 * ``unpublish`` job for every stale row against the production
 * main-branch destination on each pub. The publishing-service's
 * existing per-article unpublish handler removes the file via a
 * PR-per-article (idempotent on 404, auto-merged).
 *
 * Dry-run returns per-target counts and a top-10-worst-offenders
 * report grouped by announcement (slug list with status + rank).
 * Real run enqueues unpublish jobs and marks the article rows as
 * ``publish_status='unpublished'`` with an audit reason so reruns
 * are no-ops.
 *
 * Scope:
 *  - Production main branches only (test files are not SEO-relevant).
 *  - No 301 redirects emitted — the cascade-generated duplicates are
 *    only hours old and not yet indexed; 404 is acceptable.
 *  - Stream 2's sticky-slug fix prevents recurrence, so this is a
 *    one-shot cleanup.
 */
export async function runCleanupStaleSlugs(
  dryRun: boolean,
  options: { since?: string } = {},
): Promise<ToolRunSubmission> {
  const params: Record<string, string | number | boolean> = { dry_run: dryRun };
  if (options.since) params.since = options.since;
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/cleanup-stale-published-slugs',
    null,
    { params },
  );
  return response.data;
}

/**
 * Cleanup one-shot for the 2026-05-26 ticker-headline-leak regression.
 *
 * Walks articles generated since `since` (default 2026-05-25) whose title
 * starts with the entity's canonical_identifier (e.g. "RDM Column Leach
 * Tests Deliver..." for entity Red Metal, ticker RDM). Excludes false
 * positives where the title already starts with the company's
 * display_name (e.g. "ADX Energy Flows..." where the ticker "ADX" is also
 * the first word of the trading name).
 *
 * For each match: rewrites the title to lead with
 * COALESCE(editorial_preferred_name, display_name), NULLs prepared_content
 * (publish flow re-renders frontmatter), enqueues a republish-to-test
 * job for published articles. Does NOT enqueue promote_article — the
 * operator triggers the site-level promote (test → main) manually when
 * they're satisfied with the test build.
 *
 * Optionally regenerates social drafts attached to fixed articles
 * (`include_socials=true`) so the social-post composed_text picks up the
 * new title via publishing-service's existing /social/drafts/:id/regenerate.
 *
 * Deterministic — no AI cost. Dry-run returns a preview of up to 50
 * proposed rewrites plus summary counts by product / publish_status, and
 * informational counts of already-posted socials (irreversible) and
 * already-sent emails (no-action — email subjects use company_name, not
 * article_title).
 */
export async function runRewriteTickerLedTitles(
  dryRun: boolean,
  options: { since?: string; includeSocials?: boolean } = {},
): Promise<ToolRunSubmission> {
  const params: Record<string, string | number | boolean> = { dry_run: dryRun };
  if (options.since) params.since = options.since;
  if (options.includeSocials !== undefined) params.include_socials = options.includeSocials;
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/rewrite-ticker-led-titles',
    null,
    { params },
  );
  return response.data;
}

/**
 * Backfill Gemini name_embedding for the ~5,500 existing contact rows in
 * Cynapse's global_entities. Cut B Pass 2 ships embedding-on-ingest so
 * new contacts get embedded automatically; this tool catches up the
 * historical backlog that pre-dates the ingest wiring (Ollama era + earlier).
 *
 * Orchestrated server-side as a background task that loops the Cynapse
 * /api/contacts/backfill-embeddings endpoint in batches of 200, emitting
 * per-batch progress over the assayerai:tools WebSocket channel.
 *
 * Dry-run returns the count of contacts still NULL-embedded without
 * spending Vertex credits. Real run charges ~0.02 USD against the
 * text-embedding-004 budget for the full backlog.
 */
export async function runBackfillContactEmbeddings(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-contact-embeddings',
    null,
    { params: { dry_run: dryRun } },
  );
  return response.data;
}

/**
 * Sibling of runBackfillContactEmbeddings — operates on entity_type='company'
 * (~2,800 rows). Same Vertex/Gemini pipeline; embeds on name + industry hint
 * rather than name + org. Cheap (~0.01 USD per full run).
 */
export async function runBackfillCompanyEmbeddings(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-company-embeddings',
    null,
    { params: { dry_run: dryRun } },
  );
  return response.data;
}

/**
 * M1 B.3 — backfill visual metrics + hero/body eligibility on Cynapse
 * images. Walks ~19k existing images that landed before migration 014
 * (visual_metrics_at IS NULL) plus the small tail uploaded between the
 * B.1 deploy and the B.2 wiring (eligibility-only fix-up). For each
 * image: downloads from GCS, runs scipy.ndimage.laplace + numpy.std,
 * persists the three signals, recomputes hero/body eligibility.
 * Long-running (~80 min full sweep). Dry-run reports candidate counts.
 * Resumable — re-running after completion is a no-op via the IS NULL
 * predicate.
 */
export async function runBackfillVisualMetrics(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/backfill-visual-metrics',
    null,
    { params: { dry_run: dryRun } },
  );
  return response.data;
}

/**
 * 2026-08-26 — re-run the eligibility gates over rows that were ALREADY
 * assessed. Deliberately separate from runBackfillVisualMetrics, whose
 * eligibility cohort is scoped to `IS NULL` flags: a row marked eligible under
 * older thresholds is invisible to it, so gates added later never bind. Pure
 * recompute over stored signals — no GCS, no AI, minutes not hours.
 */
export async function runRecomputeEligibility(dryRun: boolean): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/recompute-eligibility',
    null,
    { params: { dry_run: dryRun } },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// CT-3 tranche 3a — per-entity coverage consolidation
//
// Ownership is decided per announcement, so a company whose filings scatter
// across commodity buckets gets a different indexed owner per filing and no
// masthead accumulates a coherent body of coverage. These bring already-
// published coverage under ONE owner, per entity, on operator trigger.
// ---------------------------------------------------------------------------

export interface ConsolidationCandidate {
  entity_id: string;
  display_name: string | null;
  entity_sector: string | null;
  sector_confidence: number | null;
  sector_source: string | null;
  published_articles: number;
  articles_routing_differently: number;
  would_demote: number;
  promote_candidates: number;
  owners_now: string[];
  hold_reason?: string;
}

/**
 * Progress written by the consolidation runner. The PHASE is the useful
 * part: the usual run is a single entity, and almost all of its wall-clock
 * sits inside one article's `gating` model call.
 */
export interface ConsolidationProgress {
  entities_processed?: number;
  entities_total?: number;
  current_entity_id?: string | null;
  articles_processed?: number;
  articles_total?: number | null;
  phase?: 'queued' | 'starting' | 'planning' | 'gating' | 'entity_complete' | 'complete' | string;
  totals?: Record<string, number>;
}

export interface ConsolidationSet {
  confidence_floor: number;
  entities_in_set: number;
  eligible_count: number;
  held_for_triage_count: number;
  articles_in_set: number;
  eligible: ConsolidationCandidate[];
  held_for_triage: ConsolidationCandidate[];
}

/**
 * The consolidation SET: entities whose masthead ownership changes under the
 * entity sector axis. Read-only — ownership is computed through the same
 * story_owner() the tier decision uses, once with the entity axis and once
 * without, so this can never drift from what the pass will actually do.
 */
export async function getConsolidationCandidates(
  confidenceFloor = 0.65,
): Promise<ConsolidationSet> {
  const response = await apiClient.get<ConsolidationSet>(
    '/tools/consolidation-candidates',
    { params: { confidence_floor: confidenceFloor } },
  );
  return response.data;
}

/**
 * Consolidate the named entities' published coverage onto one indexed owner.
 *
 * Demotions apply freely (a non-owner copy stays live and hub-linked, it just
 * carries noindex). Promotions do NOT: a pre-Track-I copy on the owning
 * masthead has never met the standard, so it runs the A12 quality gate here
 * and earns the index tier or keeps noindex on the verdict.
 *
 * dry_run=true writes nothing AND runs no gate, so it costs no tokens — its
 * promotion candidates report as 'would_gate_then_decide'.
 */
export async function submitConsolidateEntityCoverage(
  entityIds: string[],
  dryRun: boolean,
  opts?: {
    confidenceFloor?: number;
    gatePromotions?: boolean;
    maxGateRuns?: number;
  },
): Promise<ToolRunSubmission> {
  const response = await apiClient.post<ToolRunSubmission>(
    '/tools/consolidate-entity-coverage',
    { entity_ids: entityIds },
    {
      params: {
        dry_run: dryRun,
        confidence_floor: opts?.confidenceFloor ?? 0.65,
        gate_promotions: opts?.gatePromotions ?? true,
        max_gate_runs: opts?.maxGateRuns ?? 20,
      },
    },
  );
  return response.data;
}


/**
 * A durable backfill job (mig 378): queued as a pipeline job, so it survives a
 * backend deploy and resumes from its cursor. Unlike `waitForToolRun`, whose
 * state lives in the backend's memory, this reads the job row.
 */
export interface MaintenanceJobStatus {
  job_id: string;
  task: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  attempts: number;
  max_attempts: number;
  dry_run?: boolean;
  progress?: { done?: number; total?: number; cursor?: any; counts?: Record<string, number> };
  result?: Record<string, any> | null;
  error?: string | null;
  locked_by?: string | null;
}

export async function getMaintenanceJob(jobId: string): Promise<MaintenanceJobStatus> {
  const { data } = await apiClient.get<MaintenanceJobStatus>(`/tools/jobs/${jobId}`);
  return data;
}

/** Poll a durable job to completion; `onProgress` gets each status. */
export async function waitForMaintenanceJob<T = Record<string, any>>(
  jobId: string,
  onProgress?: (s: MaintenanceJobStatus) => void,
  intervalMs = 3000,
): Promise<T> {
  for (;;) {
    const s = await getMaintenanceJob(jobId);
    onProgress?.(s);
    if (s.status === 'completed') return (s.result ?? {}) as T;
    if (s.status === 'failed' && s.attempts >= s.max_attempts) throw new Error(s.error || 'Job failed');
    await new Promise(r => setTimeout(r, intervalMs));
  }
}
